import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getResourceById } from "./resourcesData";
import SnackbarNotification from "../shared/SnackbarNotification/SnackbarNotification";

const ResourceDownloadPage = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    organization: "",
    expectedRaise: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  useEffect(() => {
    window.scrollTo(0, 0);
    const foundResource = getResourceById(resourceId);
    if (foundResource) {
      setResource(foundResource);
    } else {
      // Resource not found, redirect to resources page
      navigate("/resources");
    }
  }, [resourceId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Check if resource has a valid download URL
      if (!resource.downloadUrl || resource.downloadUrl === "#") {
        throw new Error("Download link not available for this resource");
      }

      // Create a temporary link element to trigger download
      const link = document.createElement("a");
      link.href = resource.downloadUrl;
      link.download = `${resource.title}.pdf`;
      link.target = "_blank";

      // Append to body, click, then remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      setSnackbarMessage(
        "Download started! The file should open in a new tab."
      );
      setSnackbarType("success");
      setShowSnackbar(true);

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        organization: "",
        expectedRaise: "",
      });

      // Redirect to resource detail page after a short delay
      setTimeout(() => {
        navigate(`/resources/${resourceId}`);
      }, 2000);
    } catch (error) {
      console.error("Download failed:", error);
      setSnackbarMessage("Download failed. Please try again.");
      setSnackbarType("error");
      setShowSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  if (!resource) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Resource Not Found
          </h1>
          <Link
            to="/resources"
            className="text-blue hover:text-blue-dark font-medium"
          >
            ← Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-[100px]">
      {/* Header */}
      <div className="bg-blue py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Link
            to={`/resources/${resourceId}`}
            className="text-white hover:text-blue-100 flex items-center justify-center gap-2 mb-6"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Resource
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-inter">
            Download {resource.title}
          </h1>
          <p className="text-lg md:text-xl text-white max-w-3xl mx-auto">
            Get instant access to this free resource by filling out the form
            below.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Resource Preview */}
            <div className="bg-gray-50 p-6 border-b">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img
                    src={resource.image}
                    alt={resource.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div
                    className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center"
                    style={{ display: "none" }}
                  >
                    <svg
                      className="w-8 h-8 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-left">
                  <span className="text-orange text-sm font-medium bg-orange-100 px-3 py-1 rounded-full inline-block">
                    {resource.category}
                  </span>
                  <h2 className="text-xl font-bold text-gray-900 mt-2 text-left">
                    {resource.title}
                  </h2>
                </div>
              </div>
            </div>

            {/* Download Form */}
            <form onSubmit={handleSubmit} className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-left">
                Get Your Free Download
              </h3>

              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="E.g. Jon"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="E.g. Doe"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="E.g. jondoe@gmail.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization *
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    placeholder="E.g. Sponsor a Pet"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How much will you raise online in the next 12 months? *
                  </label>
                  <select
                    name="expectedRaise"
                    value={formData.expectedRaise}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent"
                    required
                  >
                    <option value="">Select an option</option>
                    <option value="under-10k">Under $10,000</option>
                    <option value="10k-50k">$10,000 - $50,000</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="over-100k">Over $100,000</option>
                  </select>
                </div>
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Download Now
                    </>
                  )}
                </button>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  By downloading this resource, you agree to receive occasional
                  emails from Sponsor a Pet. You can unsubscribe at any time.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Snackbar Notification */}
      <SnackbarNotification
        isOpen={showSnackbar}
        message={snackbarMessage}
        type={snackbarType}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default ResourceDownloadPage;
